<template>
  <div class="h-dvh flex flex-col overflow-hidden">
    <!-- The dark navbar is the chrome everywhere; the verification nudge sits
         under it so the chrome stays the top edge. The create-video editor is
         full-bleed below: it fills the viewport and scrolls its own panels, so
         the page must not scroll behind it. A visitor on a marketing page gets the
         glass bar instead, floating over the page (which pads by --nav-h). -->
    <TopBar v-if="authStore.isAuthenticated && !route.meta.ownBar" />
    <EmailVerificationBanner />
    <div class="relative flex min-h-0 flex-1 flex-col">
      <MarketingTopBar v-if="glassNav" :scrolled="scrolled" />
      <div
        ref="scroller"
        class="flex-1 min-h-0"
        :class="[
          fullBleed ? 'flex flex-col overflow-hidden' : 'overflow-y-auto overflow-x-hidden overscroll-y-none',
          glassNav ? '[--nav-h:5rem] sm:[--nav-h:6rem]' : '[--nav-h:0px]',
        ]"
        @scroll.passive="scrolled = ($event.target as HTMLElement).scrollTop > 8"
      >
        <router-view />
      </div>
    </div>

    <!-- One look for every toast (the alerts redesign): a white card, the tone
         in its icon chip (ToastIcons.ts, style.css), several stacked apart so each
         reads. Timings: utils/toast.ts. -->
    <Toaster close-button expand :duration="3500" :visible-toasts="3" position="bottom-right">
      <template #success-icon><component :is="TOAST_ICONS.success" /></template>
      <template #error-icon><component :is="TOAST_ICONS.error" /></template>
      <template #warning-icon><component :is="TOAST_ICONS.warning" /></template>
      <template #info-icon><component :is="TOAST_ICONS.info" /></template>
      <template #close-icon><component :is="TOAST_ICONS.close" /></template>
    </Toaster>
    <AccountSettingsModal v-if="authStore.isAuthenticated" />
    <!-- Not over the editor, whose footer buttons sit in the same corner; a visitor meets it on the way there. -->
    <CookieBanner v-if="!fullBleed" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import EmailVerificationBanner from '@/components/EmailVerificationBanner.vue'
import TopBar from '@/components/TopBar.vue'
import MarketingTopBar from '@/components/MarketingTopBar.vue'
import AccountSettingsModal from '@/components/AccountSettingsModal.vue'
import CookieBanner from '@/components/legal/CookieBanner.vue'
import { Toaster } from 'vue-sonner'
import { TOAST_ICONS } from '@/components/ToastIcons'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const authStore = useAuthStore()

const fullBleed = computed(() => route.meta.fullBleed === true)
const glassNav = computed(() => route.meta.marketing === true && !authStore.isAuthenticated)
const scrolled = ref(false)

// Pages scroll inside this div, not the window, so the router's scrollBehavior can't reach it: a new page would
// open at the previous page's offset (a footer link landing at the next page's footer), and a #section link
// (/terms#termination) wouldn't land. So a new page opens at its #section if it has one, else at the top.
const scroller = ref<HTMLElement | null>(null)
watch(
  () => [route.path, route.hash],
  async ([path], old) => {
    await nextTick()
    const section = route.hash ? document.getElementById(decodeURIComponent(route.hash.slice(1))) : null
    if (section) section.scrollIntoView()
    else if (path !== old?.[0] && scroller.value) scroller.value.scrollTop = 0
  },
)
</script>
